import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../api";
import * as XLSX from "xlsx";
import useIsOnline, { OfflineViewingCachedDataBanner } from "../useIsOnline";
import { downloadBlob } from "../nativeDownload";

const HCP_REVIEW_FORMATS = ['Singles Stableford', 'Singles Medal', 'Singles Stroke Play'];
// Moved here from Players.jsx per Mike's 2026-09-14 request -- Tee Colour
// belongs alongside the groups/tee-times it actually affects, not the
// roster page.
const TEE_COLORS = ['White', 'Yellow (Men)', 'Yellow (Ladies)', 'Red (Men)', 'Red (Ladies)'];
const PAIR_FORMATS = [
  "Better Ball Stableford", "Better Ball Medal", "Better Ball Stroke Play", "Better Ball Match Play",
  "Fourball Better Ball Stableford", "Fourball Match Play", "Fourball Stroke Play",
  "Foursomes Stableford", "Foursomes Medal", "Foursomes Match Play",
  "Greensomes Stableford", "Greensomes Medal", "Greensomes Match Play",
];

export default function Groups() {
  const isOnline = useIsOnline();
  const [players, setPlayers] = useState([]);
  const [bulkTeeColor, setBulkTeeColor] = useState("");
  const [event, setEvent] = useState(null);
  const [courses, setCourses] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [buggySummary, setBuggySummary] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [markers, setMarkers] = useState({});
  const [markerSaved, setMarkerSaved] = useState(false);
  const [savedMarkerIds, setSavedMarkerIds] = useState({});
  const [editHcp, setEditHcp] = useState({});
  const [ghost, setGhost] = useState(null);
  const [ghostEligible, setGhostEligible] = useState(false);
  const [ghostBusy, setGhostBusy] = useState(false);
  const [ghostMsg, setGhostMsg] = useState("");
  const [draggedPlayer, setDraggedPlayer] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [snapshot, setSnapshot] = useState({ exists: false });
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  const [firstTeeTime, setFirstTeeTime] = useState("09:00");
  const [teeInterval, setTeeInterval] = useState(8);
  const [allSocietyPlayers, setAllSocietyPlayers] = useState([]);
  const [replacingId, setReplacingId] = useState(null);
  const [replaceBusy, setReplaceBusy] = useState(false);

  useEffect(() => { loadGroups(); loadTeams(); loadAllSocietyPlayers(); loadCourses(); }, []);

  const loadAllSocietyPlayers = async () => {
    try {
      const res = await api.get("/players");
      setAllSocietyPlayers(res.data || []);
    } catch(e) { console.error("Load society players:", e.message); }
  };

  const loadCourses = async () => {
    try {
      const res = await api.get("/courses");
      setCourses(res.data || []);
    } catch(e) { console.error("Load courses:", e.message); }
  };

  // First group tees off at firstTeeTime; each subsequent group follows at
  // teeInterval minutes later — replaces manually typing a time per group.
  const addMinutesToTime = (timeStr, minutesToAdd) => {
    const [h, m] = (timeStr || "09:00").split(":").map(Number);
    let total = ((h || 0) * 60 + (m || 0) + minutesToAdd) % 1440;
    if (total < 0) total += 1440;
    const hh = Math.floor(total / 60).toString().padStart(2, "0");
    const mm = (total % 60).toString().padStart(2, "0");
    return `${hh}:${mm}`;
  };
  const teeTimeForGroup = (grp) => addMinutesToTime(firstTeeTime, ((grp || 1) - 1) * (parseInt(teeInterval) || 0));

  const saveTeeTimes = async (newFirst, newInterval) => {
    if (!event) return;
    try {
      await api.put(`/events/${event.id}/teetimes`, { first_tee_time: newFirst, tee_interval_minutes: parseInt(newInterval) || 0 });
    } catch(e) { console.error("Save tee times:", e.message); }
  };

  const loadTeams = async () => {
    try {
      const res = await api.get("/teams");
      setTeams(res.data.teams || []);
    } catch(e) { console.error("Load teams:", e.message); }
  };

  const saveHcp = async (player, hcp) => {
    try {
      await api.put("/players/" + player.player_id, { name: player.name, handicap: parseInt(hcp) || 0 });
      setPlayers(prev => prev.map(p => p.player_id === player.player_id ? { ...p, handicap: parseInt(hcp) || 0 } : p));
      setEditHcp(prev => { const n = {...prev}; delete n[player.player_id]; return n; });
    } catch(e) { console.error("Save HCP error:", e); }
  };

  const loadGroups = async () => {
    try {
      const res = await api.get("/groups");
      setEvent(res.data.event);
      if (res.data.event?.first_tee_time) setFirstTeeTime(res.data.event.first_tee_time);
      if (res.data.event?.tee_interval_minutes != null) setTeeInterval(res.data.event.tee_interval_minutes);
      const p = res.data.players || [];
      setPlayers(p);
      setGhost(res.data.ghost || null);
      setGhostEligible(!!res.data.ghostEligible);
      setSnapshot(res.data.snapshot || { exists: false });
      const m = {};
      p.forEach(pl => { if (pl.marker_player_id) m[pl.player_id] = pl.marker_player_id; });
      setMarkers(m);
    } catch(e) { setError("Failed to load groups"); }
    finally { setLoading(false); }
  };

  const autoGroup = async () => {
    try {
      setLoading(true);
      const res = await api.post("/groups/autogroup");
      setEvent(res.data.event);
      setPlayers(res.data.players || []);
      if (res.data.buggySummary) setBuggySummary(res.data.buggySummary);
    } catch(e) { setError("Failed to auto-group: " + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  };

  // Every edit on this page already persists itself immediately (drag,
  // Auto Group, editing a group number, ticking Buggy) — this is a
  // deliberate checkpoint instead: a bookmark of the exact current setup
  // that "Restore Last Group" can snap straight back to. The get-out-of-
  // jail card for a change made in a hurry right before the match that
  // turns out to be wrong. One checkpoint per event — saving again
  // overwrites the previous bookmark, it doesn't keep a history.
  const saveGroups = async () => {
    try {
      const res = await api.post("/groups/snapshot");
      setSnapshot({ exists: true, created_at: res.data.created_at });
      alert("Groups saved! You can restore this exact setup later with Restore Last Group.");
    } catch(e) { setError("Failed to save groups"); }
  };

  const restoreLastGroup = async () => {
    if (!window.confirm("Restore the last saved group setup? This replaces everything on screen now, including any changes made since you last clicked Save Groups.")) return;
    setSnapshotBusy(true);
    try {
      const res = await api.post("/groups/snapshot/restore");
      setPlayers(res.data.players);
    } catch(e) { setError("Failed to restore: " + (e.response?.data?.error || e.message)); }
    finally { setSnapshotBusy(false); }
  };

  const addGhost = async () => {
    setGhostBusy(true); setGhostMsg("");
    try {
      const res = await api.post("/groups/add-ghost");
      setGhost(res.data.ghost);
      const groups = res.data.threeballGroups || [];
      setGhostMsg(groups.length
        ? `${res.data.ghost.name} (HCP ${res.data.ghost.handicap}) is the ghost — filling group${groups.length !== 1 ? "s" : ""} ${groups.join(", ")}`
        : `${res.data.ghost.name} (HCP ${res.data.ghost.handicap}) is the ghost, but no groups are currently stuck at 3.`);
      setTimeout(() => setGhostMsg(""), 6000);
    } catch(e) { setError("Failed to set ghost: " + (e.response?.data?.error || e.message)); }
    finally { setGhostBusy(false); }
  };

  const removeGhost = async () => {
    setGhostBusy(true); setGhostMsg("");
    try {
      await api.post("/groups/remove-ghost");
      setGhost(null);
      setGhostMsg("Ghost removed");
      setTimeout(() => setGhostMsg(""), 3000);
    } catch(e) { setError("Failed to remove ghost: " + (e.response?.data?.error || e.message)); }
    finally { setGhostBusy(false); }
  };

  const saveMarkers = async () => {
    try {
      const assignments = Object.entries(markers).map(([player_id, marker_player_id]) => ({
        player_id: parseInt(player_id), marker_player_id: parseInt(marker_player_id)
      })).filter(a => a.marker_player_id);
      await api.post("/groups/markers", { assignments });
      setMarkerSaved(true);
      setTimeout(() => setMarkerSaved(false), 2500);
    } catch(e) {
      setError("Failed to save markers: " + (e.response?.data?.error || e.message));
    }
  };

  const autoSaveMarker = async (playerId, markerPlayerId) => {
    if (!markerPlayerId) return;
    try {
      await api.post("/groups/markers", { assignments: [{ player_id: parseInt(playerId), marker_player_id: parseInt(markerPlayerId) }] });
      setSavedMarkerIds(prev => ({ ...prev, [playerId]: true }));
      setTimeout(() => setSavedMarkerIds(prev => { const n = {...prev}; delete n[playerId]; return n; }), 2000);
    } catch(e) { setError("Auto-save marker failed"); }
  };

  const autoAssignMarkers = async () => {
    // Pair up in twos: A marks B AND B marks A (real golf card swap)
    const newMarkers = {};
    const byGroup = {};
    playingPlayers.forEach(p => {
      if (!p.hcp_review) return;
      if (!byGroup[p.grp]) byGroup[p.grp] = [];
      byGroup[p.grp].push(p);
    });
    Object.values(byGroup).forEach(grpPlayers => {
      // Pair up in twos: A marks B AND B marks A (real golf card swap)
      for (let i = 0; i + 1 < grpPlayers.length; i += 2) {
        const a = grpPlayers[i];
        const b = grpPlayers[i + 1];
        newMarkers[a.player_id] = b.player_id; // B marks A
        newMarkers[b.player_id] = a.player_id; // A marks B
      }
      // Odd player out: pairs with the one before
      if (grpPlayers.length % 2 !== 0 && grpPlayers.length > 1) {
        const last = grpPlayers[grpPlayers.length - 1];
        const prev = grpPlayers[grpPlayers.length - 2];
        newMarkers[last.player_id] = prev.player_id;
      }
    });
    if (Object.keys(newMarkers).length === 0) { setError("No HCP review players with group members to assign"); return; }
    setMarkers(prev => ({ ...prev, ...newMarkers }));
    try {
      const assignments = Object.entries(newMarkers).map(([pid, mid]) => ({ player_id: parseInt(pid), marker_player_id: parseInt(mid) }));
      await api.post("/groups/markers", { assignments });
      setMarkerSaved(true);
      setTimeout(() => setMarkerSaved(false), 2500);
    } catch(e) { setError("Auto-assign failed: " + (e.response?.data?.error || e.message)); }
  };

  const exportToExcel = () => {
    if (playingPlayers.length === 0) { setError("No players to export"); return; }
    const sheetData = playingPlayers.map(p => {
      const row = {
        "Member": p.name + (p.captain ? " (C)" : ""),
        "Handicap": p.handicap,
        "Group": p.grp || "-",
        "Tee Off": p.grp ? teeTimeForGroup(p.grp) : "-",
        "Buggy": p.buggy ? "Yes" : "No",
      };
      if (showTeamCol) row["Team"] = getTeamName(p);
      if (showMarkerCol) {
        row["HCP Review"] = p.hcp_review ? "Yes" : "No";
        const markerId = markers[p.player_id];
        const marker = markerId ? players.find(pl => pl.player_id === markerId) : null;
        row["Marker Assigned"] = marker ? marker.name : "";
      }
      return row;
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheetData), "Groups");
    downloadBlob(new Blob([XLSX.write(wb, { type: "array", bookType: "xlsx" })], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "Groups.xlsx");
  };

  const startEdit = (player) => { setEditingId(player.id); setEditValue(player.grp ? String(player.grp) : ""); };

  const commitEdit = async (player) => {
    const grp = parseInt(editValue);
    if (!isNaN(grp) && grp > 0) {
      try {
        await api.post("/groups/save", { groups: [{ id: player.id, grp, bingo_number: player.bingo_number }] });
        setPlayers(prev => [...prev.map(p => p.id === player.id ? { ...p, grp } : p)].sort((a, b) => (a.grp || 0) - (b.grp || 0) || a.name.localeCompare(b.name)));
      } catch(e) { console.error(e); }
    }
    setEditingId(null); setEditValue("");
  };

  // Drag-and-drop group reassignment. Prefer a plain move (the dragged
  // player joins the target group, nobody displaced) whenever that's safe —
  // e.g. dragging from a 4-ball onto a 3-ball fills it to a 4-ball, exactly
  // as you'd expect. Only fall back to a SWAP (exchanging the two players'
  // groups) when a plain move would break the "always 3 or 4" rule — moving
  // out of an already-3 group, or into an already-4 group. A swap can never
  // change either group's size, so that fallback is always safe.
  //
  // Players not yet assigned a group (grp null/0 — e.g. added after Auto
  // Group already ran) are NOT a real group and must be exempt from the
  // 3/4 sizing rule entirely: leaving "unassigned" never breaks a real
  // group, and joining "unassigned" never overfills one. Without this,
  // several unassigned players get counted as a fake same-sized "group",
  // which can wrongly force a swap and evict an existing player out of a
  // real group instead of just adding the dragged player in.
  const dropOnPlayer = async (a, b) => {
    if (a.grp === b.grp) return;
    const aUnassigned = !a.grp || a.grp <= 0;
    const bUnassigned = !b.grp || b.grp <= 0;
    const sourceSize = aUnassigned ? 0 : playingPlayers.filter(p => p.grp === a.grp).length;
    const targetSize = bUnassigned ? 0 : playingPlayers.filter(p => p.grp === b.grp).length;
    const canPlainMove = (aUnassigned || sourceSize - 1 >= 3) && (bUnassigned || targetSize + 1 <= 4);
    try {
      if (canPlainMove) {
        await api.post("/groups/save", { groups: [{ id: a.id, grp: b.grp, bingo_number: a.bingo_number }] });
      } else {
        await api.post("/groups/save", { groups: [
          { id: a.id, grp: b.grp, bingo_number: a.bingo_number },
          { id: b.id, grp: a.grp, bingo_number: b.bingo_number },
        ] });
      }
      // A plain move can change which group is the 3-ball vs a 4-ball (a
      // swap never does — it trades players one-for-one, so sizes don't
      // move) — renumber so group order still reflects "3-balls and buggy
      // groups tee off first," without touching who's grouped with whom or
      // bumping Michael/Neil/Clive out of their locked Group 1/2 slot.
      const res = await api.post("/groups/reorder");
      setPlayers(res.data.players);
    } catch(e) { console.error(e); setError("Failed to move player"); }
  };

  const handleDragStart = (e, player) => {
    setDraggedPlayer(player);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (e, player) => {
    e.preventDefault();
    if (dragOverId !== player.id) setDragOverId(player.id);
  };
  const handleDragLeave = () => setDragOverId(null);
  const handleDrop = (e, targetPlayer) => {
    e.preventDefault();
    setDragOverId(null);
    if (draggedPlayer && draggedPlayer.id !== targetPlayer.id) dropOnPlayer(draggedPlayer, targetPlayer);
    setDraggedPlayer(null);
  };

  // Only offer a Tee Color that genuinely exists for the linked course --
  // same rule Players.jsx used before this moved here (gender:null on a tee
  // means we can't rule either sex out, so both labels are offered). No
  // course linked, or a course with no stored tee data, falls back to
  // showing every colour.
  const linkedCourse = courses.find(c => c.id === event?.course_id);
  const getAvailableTeeColors = (course) => {
    if (!course || !course.tee_sets || course.tee_sets.length === 0) return TEE_COLORS;
    const available = new Set();
    course.tee_sets.forEach(t => {
      const colour = (t.colour || "").trim().toLowerCase();
      const gender = t.gender;
      if (colour === "white") available.add("White");
      else if (colour === "yellow") {
        if (gender === "Female") available.add("Yellow (Ladies)");
        else if (gender === "Male") available.add("Yellow (Men)");
        else { available.add("Yellow (Men)"); available.add("Yellow (Ladies)"); }
      } else if (colour === "red") {
        if (gender === "Female") available.add("Red (Ladies)");
        else if (gender === "Male") available.add("Red (Men)");
        else { available.add("Red (Men)"); available.add("Red (Ladies)"); }
      }
    });
    return TEE_COLORS.filter(c => available.has(c));
  };
  const availableTeeColors = getAvailableTeeColors(linkedCourse);
  const teeColorOptionsFor = (player) => player.tee_color && !availableTeeColors.includes(player.tee_color)
    ? [...availableTeeColors, player.tee_color]
    : availableTeeColors;

  const saveTeeColor = async (player, value) => {
    try {
      // Minimal payload, matching saveHcp above -- name+handicap must always
      // be included since the backend writes them unconditionally, but never
      // spread the whole groups-shaped player object here: it's missing
      // players-table fields like `team`, which the route would otherwise
      // silently blank.
      await api.put("/players/" + player.player_id, { name: player.name, handicap: player.handicap, tee_color: value });
      setPlayers(prev => prev.map(p => p.id === player.id ? { ...p, tee_color: value } : p));
    } catch(e) { console.error("Save tee colour:", e.message); }
  };

  // Header dropdown -- sets every currently-playing player's Tee Colour in
  // one go, same "Set all..." convenience Players.jsx had before this moved
  // here. Confirmed first since it touches everyone shown in this table.
  const applyBulkTeeColor = async (value) => {
    if (!value) { setBulkTeeColor(""); return; }
    if (!window.confirm(`Set Tee Colour to "${value}" for all ${playingPlayers.length} players?`)) return;
    setBulkTeeColor(value);
    try {
      await Promise.all(playingPlayers.map(p => api.put("/players/" + p.player_id, { name: p.name, handicap: p.handicap, tee_color: value })));
      setPlayers(prev => prev.map(p => p.playing ? { ...p, tee_color: value } : p));
    } catch(e) { console.error("Bulk tee colour failed:", e.message); setError("Failed to set tee colour for all players"); }
  };

  const updateBuggy = async (player, value) => {
    try {
      await api.post("/events/" + event.id + "/players", {
        player_id: player.player_id, playing: player.playing, buggy: value, captain: player.captain, vice_captain: player.vice_captain, hcp_review: player.hcp_review || false,
      });
      setPlayers(prev => prev.map(p => p.id === player.id ? { ...p, buggy: value } : p));
    } catch(e) { console.error("Update buggy:", e.message); }
  };

  // Real-world drop-out handling — remove a player straight from the Groups
  // page (matches how it actually happens on the day) rather than making
  // the organiser go back to Players to untick them. Reuses the same
  // playing-flag update the Players page checkbox already does, so the two
  // screens can never disagree about who's actually playing.
  const removePlayer = async (player) => {
    if (!window.confirm(`Remove ${player.name} from today's event? This unticks them as Playing — they can be added back from the Players page.`)) return;
    try {
      await api.post("/events/" + event.id + "/players", {
        player_id: player.player_id, playing: false, buggy: player.buggy, captain: player.captain, vice_captain: player.vice_captain, hcp_review: player.hcp_review || false,
      });
      setPlayers(prev => prev.filter(p => p.id !== player.id));
    } catch(e) { console.error("Remove player:", e.message); setError("Failed to remove player"); }
  };

  // Late substitute — the obvious real-world case of removePlayer above:
  // someone drops out and a stand-in takes their spot in the SAME group,
  // same tee time, no need to go back to Players and drag them in by hand.
  // The substitute can be anyone in the society roster not currently
  // playing today, including someone who's never been added to this event
  // at all (a fresh event_players row gets created for them via the same
  // upsert the Players screen checkbox uses).
  const replacePlayer = async (oldPlayer, newPlayerId) => {
    setReplaceBusy(true);
    try {
      await api.post("/events/" + event.id + "/players", {
        player_id: newPlayerId, playing: true, buggy: false, captain: false, vice_captain: false, hcp_review: false,
      });
      await api.post("/events/" + event.id + "/players", {
        player_id: oldPlayer.player_id, playing: false, buggy: oldPlayer.buggy, captain: oldPlayer.captain,
        vice_captain: oldPlayer.vice_captain, hcp_review: oldPlayer.hcp_review || false,
      });
      const res = await api.get("/groups");
      const newEp = (res.data.players || []).find(p => p.player_id === newPlayerId);
      if (newEp && oldPlayer.grp) {
        await api.post("/groups/save", { groups: [{ id: newEp.id, grp: oldPlayer.grp, bingo_number: oldPlayer.bingo_number }] });
      }
      setReplacingId(null);
      await loadGroups();
    } catch(e) { console.error("Replace player:", e.message); setError("Failed to replace player"); }
    finally { setReplaceBusy(false); }
  };

  const handleKey = (e, player) => {
    if (e.key === "Enter") commitEdit(player);
    if (e.key === "Escape") { setEditingId(null); setEditValue(""); }
  };

  const playingPlayers = players.filter(p => p.playing);
  const buggyCount = players.filter(p => p.buggy).length;
  const isHcpFormat = HCP_REVIEW_FORMATS.includes(event?.game_type);
  const isPairFormat = PAIR_FORMATS.includes(event?.game_type);
  const hcpReviewCount = playingPlayers.filter(p => p.hcp_review).length;

  const groupMap = {};
  playingPlayers.forEach(p => { if (!groupMap[p.grp]) groupMap[p.grp] = []; groupMap[p.grp].push(p); });
  const getGroupMembers = (player) => (groupMap[player.grp] || []).filter(p => p.player_id !== player.player_id);

  const th = "px-4 py-2 font-semibold border-r border-green-700 text-center";
  const td = "px-4 py-2 border-r border-gray-300";
  const showMarkerCol = isHcpFormat && hcpReviewCount > 0;
  const showTeamCol = teams.length >= 2;
  const getTeamName = (player) => {
    const t = teams.find(t => t.id === player.team_id);
    return t ? t.team_name : "-";
  };

  const grpSizes = {};
  playingPlayers.forEach(p => { grpSizes[p.grp] = (grpSizes[p.grp] || 0) + 1; });
  const threeballGroupNumbers = Object.keys(grpSizes).map(Number).filter(g => grpSizes[g] === 3);
  const sortedGroupNumbers = Object.keys(groupMap).map(Number).filter(g => !isNaN(g)).sort((a, b) => a - b);

  // Pair formats build groups on the Pairs page instead — that's the only
  // place team_id/pair_slot actually get set (see Pairs.jsx). Every other
  // tool this page offered for pair formats (tee times, buggy, replace,
  // Save/Restore Groups, Ghost, export/print) was ported there 2026-09-06
  // so nothing is lost, only Auto Group's own drag/edit table which never
  // applied to pair formats to begin with.
  if (isPairFormat && !loading) {
    return (
      <Layout>
        <div style={{ maxWidth: "560px", margin: "60px auto", textAlign: "center", padding: "0 16px" }}>
          <div style={{ fontSize: "32px", marginBottom: "12px" }}>🤝</div>
          <h2 style={{ fontSize: "18px", fontWeight: 700, color: "#374151", marginBottom: "8px" }}>
            {event?.game_type} groups are built on the Pairs page
          </h2>
          <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "20px", lineHeight: 1.6 }}>
            Grouping, tee times, buggy, replacing a player, Save/Restore Groups, and print/export for this
            format all live on the Pairs page now, alongside building the actual pairs.
          </p>
          <a href="/pairs" style={{ display: "inline-block", background: "#16a34a", color: "white", fontWeight: 700, fontSize: "14px", padding: "10px 20px", borderRadius: "6px", textDecoration: "none" }}>
            Go to Pairs →
          </a>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <style>{`
        .print-only { display: none; }
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          @page { margin: 15mm; }
        }
        body.printing-now .no-print { display: none !important; }
        body.printing-now .print-only { display: block !important; }
      `}</style>
      <div className="print-only">
        <h1 style={{ fontSize: "20px", fontWeight: 700, marginBottom: "2px" }}>{event?.venue || "Groups"}</h1>
        <div style={{ fontSize: "12px", color: "#555", marginBottom: "18px" }}>
          {event?.date ? new Date(event.date).toLocaleDateString("en-GB") : ""}{event?.game_type ? " — " + event.game_type : ""}
        </div>
        {sortedGroupNumbers.map(g => (
          <div key={g} style={{ marginBottom: "18px", pageBreakInside: "avoid" }}>
            <div style={{ fontSize: "16px", fontWeight: 700, background: "#166534", color: "white", padding: "5px 10px", marginBottom: "4px" }}>
              Group {g} — Tee Off {teeTimeForGroup(g)}
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <tbody>
                {groupMap[g].map(p => (
                  <tr key={p.id} style={{ borderBottom: "1px solid #ddd" }}>
                    <td style={{ padding: "5px 8px" }}>{p.name}{p.captain ? " (C)" : ""}</td>
                    <td style={{ padding: "5px 8px", textAlign: "center", width: "70px" }}>HCP {p.handicap}</td>
                    {showTeamCol && <td style={{ padding: "5px 8px", textAlign: "center", width: "110px" }}>{getTeamName(p)}</td>}
                    <td style={{ padding: "5px 8px", textAlign: "center", width: "50px" }}>{p.buggy ? "🚗" : ""}</td>
                  </tr>
                ))}
                {ghost && threeballGroupNumbers.includes(g) && (
                  <tr style={{ borderBottom: "1px solid #ddd", fontStyle: "italic", color: "#666" }}>
                    <td style={{ padding: "5px 8px" }}>👻 Ghost ({ghost.name})</td>
                    <td style={{ padding: "5px 8px", textAlign: "center" }}>HCP {ghost.handicap}</td>
                    {showTeamCol && <td></td>}
                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ))}
        {sortedGroupNumbers.length === 0 && <div style={{ color: "#888" }}>No groups assigned yet.</div>}
      </div>
      <div className="flex flex-col h-full no-print">
        {!isOnline && <OfflineViewingCachedDataBanner screen="Groups" />}
        <div className="bg-blue-50 border-b border-gray-300 px-4 py-2 flex items-center gap-4 flex-wrap">
          <span className="font-semibold text-green-800">Groups</span>
          <span className="text-sm text-gray-600">Total: {playingPlayers.length}</span>
          <span className="text-xs text-gray-400">Drag ⠿ a player onto another player's row — moves them in if there's room, otherwise swaps the two</span>
          <div className="flex items-center gap-1.5 text-xs">
            <label className="text-gray-600 font-medium">First tee</label>
            <input type="time" value={firstTeeTime}
              onChange={e => { setFirstTeeTime(e.target.value); saveTeeTimes(e.target.value, teeInterval); }}
              className="border border-gray-300 rounded px-1.5 py-0.5" />
            <label className="text-gray-600 font-medium ml-1">Interval</label>
            <input type="number" min="1" value={teeInterval}
              onChange={e => { setTeeInterval(e.target.value); saveTeeTimes(firstTeeTime, e.target.value); }}
              className="border border-gray-300 rounded px-1.5 py-0.5 w-14" />
            <span className="text-gray-500">mins</span>
          </div>
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <span className="bg-green-600 text-white text-xs font-semibold px-3 py-1 rounded">Playing: {playingPlayers.length}</span>
            <span className="bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded">Buggy: {buggyCount}</span>
            {showMarkerCol && <span className="bg-purple-600 text-white text-xs font-semibold px-3 py-1 rounded">HCP Review: {hcpReviewCount}</span>}
            <button onClick={autoGroup} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Auto Group</button>
            <button onClick={saveGroups} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Save Groups</button>
            <button onClick={restoreLastGroup} disabled={!snapshot.exists || snapshotBusy}
              title={snapshot.exists ? "Last saved " + new Date(snapshot.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : "No saved checkpoint yet — click Save Groups first"}
              className="bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-xs font-semibold px-3 py-1.5 rounded">
              {snapshotBusy ? "Restoring..." : "Restore Last Group"}
            </button>
            <button onClick={exportToExcel} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded">📊 Export Excel</button>
            <button onClick={async () => {
              if (window.electronAPI?.printPreview) {
                document.body.classList.add("printing-now");
                // printToPDF() runs in the main process and can capture the page
                // before Chromium actually repaints with the new class applied --
                // classList.add() alone doesn't guarantee the frame is painted yet.
                // Double rAF waits for one full real paint before capturing.
                await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
                try {
                  const result = await window.electronAPI.printPreview();
                  if (!result?.success) alert("Print preview failed: " + (result?.error || "unknown error"));
                } finally {
                  document.body.classList.remove("printing-now");
                }
              } else {
                window.print();
              }
            }} className="bg-slate-600 hover:bg-slate-700 text-white text-xs font-semibold px-3 py-1.5 rounded">🖨️ Print Groups</button>
            {ghostEligible && (
              <button onClick={addGhost} disabled={ghostBusy} className="bg-slate-700 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-1.5 rounded">👻 Ghost</button>
            )}
            {ghostEligible && ghost && (
              <button onClick={removeGhost} disabled={ghostBusy} className="bg-white hover:bg-gray-100 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded border border-slate-300">Remove Ghost</button>
            )}
            {showMarkerCol && (
              <button onClick={autoAssignMarkers} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Auto Assign Markers</button>
            )}
            {showMarkerCol && (
              <button onClick={saveMarkers} className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-3 py-1.5 rounded">
                {markerSaved ? "✅ Markers Saved" : "Save Markers"}
              </button>
            )}
          </div>
        </div>
        {error && <div className="bg-red-100 text-red-700 px-4 py-2 text-sm">{error}</div>}
        {ghostMsg && <div className="bg-slate-100 text-slate-700 px-4 py-2 text-sm font-medium">👻 {ghostMsg}</div>}
        {ghostEligible && ghost && !ghostMsg && (
          <div className="bg-slate-50 text-slate-600 px-4 py-2 text-xs">
            👻 Ghost: <strong>{ghost.name}</strong> (HCP {ghost.handicap}) — filling any group stuck at 3
          </div>
        )}
        {buggySummary && <div className="bg-orange-50 text-orange-700 px-4 py-2 text-sm font-medium">👥 {buggySummary}</div>}
        {showMarkerCol && (
          <div className="bg-purple-50 border-b border-purple-200 px-4 py-2 text-xs text-purple-700 font-medium">
            📋 {hcpReviewCount} player{hcpReviewCount !== 1 ? "s have" : " has"} opted for HCP Review. Use Auto Assign Markers or set manually. Saves automatically on selection.
          </div>
        )}
        {loading && <div className="bg-blue-50 text-blue-700 px-4 py-2 text-sm">Loading...</div>}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm border-collapse border border-gray-300">
            <thead className="sticky top-0">
              <tr className="bg-green-800 text-white">
                <th className={th + " text-left"} style={{width: showMarkerCol ? "20%" : "35%"}}>Member</th>
                <th className={th} style={{width:"10%"}}>Handicap</th>
                <th className={th} style={{width:"10%"}}>
                  <div className="flex flex-col items-center gap-0.5">
                    <span>Tee Colour</span>
                    <select value={bulkTeeColor} onChange={e => applyBulkTeeColor(e.target.value)}
                      title="Set this Tee Colour for every player"
                      className="border border-green-600 bg-green-700 text-white rounded px-1 py-0.5 text-[10px] font-normal">
                      <option value="">Set all...</option>
                      {availableTeeColors.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </th>
                <th className={th} style={{width:"10%"}}>Group</th>
                <th className={th} style={{width:"10%"}}>Tee Off</th>
                <th className={th} style={{width:"10%"}}>Buggy</th>
                {showTeamCol && <th className={th} style={{width:"10%"}}>Team</th>}
                {showMarkerCol && <th className={th} style={{width:"10%"}}>HCP</th>}
                {showMarkerCol && <th className="px-4 py-2 font-semibold text-left border-r border-green-700" style={{width:"30%"}}>Marker Assigned</th>}
              </tr>
            </thead>
            <tbody>
              {playingPlayers.map((player, i) => {
                const groupMembers = getGroupMembers(player);
                const isLastInGroup = i === playingPlayers.length - 1 || playingPlayers[i + 1].grp !== player.grp;
                const showGhostRow = ghost && isLastInGroup && threeballGroupNumbers.includes(player.grp);
                return (
                  <React.Fragment key={player.id}>
                  <tr
                    draggable
                    onDragStart={(e) => handleDragStart(e, player)}
                    onDragOver={(e) => handleDragOver(e, player)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, player)}
                    className={"border-b border-gray-200 "
                      + (dragOverId === player.id ? "bg-blue-100 " : (i % 2 === 0 ? "bg-white " : "bg-green-50 "))
                      + (draggedPlayer?.id === player.id ? "opacity-40" : "")}
                    style={{ cursor: "grab" }}>
                    <td className={td + " font-medium"}>
                      {replacingId === player.id ? (
                        <span className="inline-flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                          <span className="text-xs text-gray-500">Replace {player.name.split(" ")[0]} with:</span>
                          <select autoFocus disabled={replaceBusy}
                            onChange={e => { if (e.target.value) replacePlayer(player, parseInt(e.target.value)); }}
                            className="border border-gray-300 rounded px-1 py-0.5 text-xs">
                            <option value="">Choose a substitute...</option>
                            {allSocietyPlayers
                              .filter(p => !playingPlayers.some(pp => pp.player_id === p.id))
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map(p => <option key={p.id} value={p.id}>{p.name} (hcp {p.handicap})</option>)}
                          </select>
                          <button onClick={() => setReplacingId(null)} disabled={replaceBusy} className="text-gray-400 hover:text-gray-600 text-xs">Cancel</button>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="text-gray-300 select-none" title="Drag onto another player — moves in if there's room, otherwise swaps groups">⠿</span>
                          <button onClick={() => removePlayer(player)} title="Remove from today's event (drop-out)"
                            className="text-red-300 hover:text-red-600 font-bold leading-none px-0.5" style={{ fontSize: "14px" }}>✕</button>
                          <button onClick={() => setReplacingId(player.id)} title="Replace with a substitute — keeps their group and tee time"
                            className="text-blue-300 hover:text-blue-600 font-bold leading-none px-0.5" style={{ fontSize: "13px" }}>🔁</button>
                          {player.captain
                            ? <span className="inline-flex items-center gap-1">{player.name} <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-1 rounded">C</span></span>
                            : player.name}
                        </span>
                      )}
                    </td>
                    <td className={td + " text-center"}>
                      {editHcp[player.player_id] !== undefined ? (
                        <input type="number" value={editHcp[player.player_id]} autoFocus
                          onChange={e => setEditHcp(prev => ({ ...prev, [player.player_id]: e.target.value }))}
                          onBlur={() => saveHcp(player, editHcp[player.player_id])}
                          onKeyDown={e => { if (e.key === "Enter") saveHcp(player, editHcp[player.player_id]); }}
                          style={{ width: "50px", textAlign: "center", border: "1px solid #16a34a", borderRadius: "4px", padding: "2px 4px" }} />
                      ) : (
                        <span onClick={() => setEditHcp(prev => ({ ...prev, [player.player_id]: player.handicap }))}
                          style={{ cursor: "pointer", fontWeight: "600" }}
                          title="Click to edit handicap">
                          {player.handicap}
                        </span>
                      )}
                    </td>
                    <td className={td + " text-center"} onClick={e => e.stopPropagation()}>
                      <select value={player.tee_color || ""} onChange={e => saveTeeColor(player, e.target.value)}
                        className="border border-gray-300 rounded px-1 py-0.5 text-xs">
                        <option value="">—</option>
                        {teeColorOptionsFor(player).map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td className={td + " text-center"}>
                      {editingId === player.id ? (
                        <input type="number" value={editValue} onChange={e => setEditValue(e.target.value)}
                          onBlur={() => commitEdit(player)} onKeyDown={e => handleKey(e, player)}
                          className="border border-green-500 rounded px-1 py-0.5 w-16 text-xs text-center" autoFocus min="1" />
                      ) : (
                        <span className="cursor-pointer hover:bg-green-100 font-bold text-green-700 px-2 py-1 rounded"
                          onClick={() => startEdit(player)} title="Click to edit group">
                          {player.grp || "-"}
                        </span>
                      )}
                    </td>
                    <td className={td + " text-center font-medium text-green-800"}>{player.grp ? teeTimeForGroup(player.grp) : "-"}</td>
                    <td className={td + " text-center"}><input type="checkbox" checked={!!player.buggy} onChange={e => updateBuggy(player, e.target.checked)} className="w-4 h-4 cursor-pointer" /></td>
                    {showTeamCol && (
                      <td className={td + " text-center"}>
                        <span className={player.team_id ? "text-xs font-semibold text-blue-700" : "text-xs text-gray-300"}>
                          {getTeamName(player)}
                        </span>
                      </td>
                    )}
                    {showMarkerCol && (
                      <td className={td + " text-center"}>
                        {player.hcp_review
                          ? <span className="bg-purple-100 text-purple-700 text-xs font-bold px-2 py-0.5 rounded-full">✓ Review</span>
                          : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                    )}
                    {showMarkerCol && (
                      <td className="px-3 py-1.5">
                        {player.hcp_review ? (
                          player.grp ? (
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <select
                                value={markers[player.player_id] || ""}
                                onChange={e => {
                                  const val = parseInt(e.target.value) || "";
                                  setMarkers(prev => ({ ...prev, [player.player_id]: val }));
                                  if (val) autoSaveMarker(player.player_id, val);
                                }}
                                style={{ fontSize: "12px", border: "1px solid #d1d5db", borderRadius: "4px", padding: "3px 6px", flex: 1 }}>
                                <option value="">— assign marker —</option>
                                {groupMembers.map(m => (
                                  <option key={m.player_id} value={m.player_id}>{m.name}</option>
                                ))}
                              </select>
                              {savedMarkerIds[player.player_id] && (
                                <span style={{ fontSize: "11px", color: "#16a34a", fontWeight: "700", whiteSpace: "nowrap" }}>✓ saved</span>
                              )}
                            </div>
                          ) : <span className="text-xs text-gray-400">Assign group first</span>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                    )}
                  </tr>
                  {showGhostRow && (
                    <tr className="border-b border-gray-200 bg-slate-100">
                      <td className={td + " font-medium italic text-slate-600"}>👻 Ghost ({ghost.name})</td>
                      <td className={td + " text-center italic text-slate-500"}>{ghost.handicap}</td>
                      <td className={td + " text-center italic text-slate-500"}>{player.grp}</td>
                      <td className={td + " text-center text-slate-300 text-xs"}>—</td>
                      <td className={td + " text-center text-slate-300 text-xs"}>—</td>
                      {showTeamCol && <td className={td + " text-center text-slate-300 text-xs"}>—</td>}
                      {showMarkerCol && <td className={td + " text-center text-slate-300 text-xs"}>—</td>}
                      {showMarkerCol && <td className="px-3 py-1.5 text-slate-300 text-xs">—</td>}
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {!loading && playingPlayers.length === 0 && (
            <div className="text-center py-8 text-gray-500">No playing players. Go to Players screen and tick the Playing checkbox.</div>
          )}
        </div>
      </div>
    </Layout>
  );
}
